import type { utils } from 'blockly/core';

import { categoryCssConfig } from '../categoryCssConfig.js';
import { buildBeginnerDisplayCategory } from './beginner-display-category.js';
import { buildBeginnerLedCategory } from './beginner-led-category.js';
import { buildBeginnerSerialMonitorCategory } from './beginner-serial-category.js';
import { buildBeginnerSoundCategory } from './beginner-sound-category.js';
import { buildBeginnerSwitchCategory } from './beginner-switch-category.js';
import {
  BEGINNER_BASIC_COLOR,
  BEGINNER_TIME_COLOR,
  LOGIC_COLOR,
  LOOPS_COLOR,
  MATH_COLOR,
  VAR_COLOR,
} from './constants.js';
type ToolboxInfo = utils.toolbox.ToolboxInfo;

/** Beginner toolbox — simplified blocks without low-level Arduino details. */
export function buildBeginnerToolbox(boardId = ''): ToolboxInfo {
  return {
    kind: 'categoryToolbox',
    contents: [
      {
        kind: 'category',
        name: 'Basic',
        colour: String(BEGINNER_BASIC_COLOR),
        cssconfig: categoryCssConfig('text'),
        contents: [
          { kind: 'block', type: 'beginner_start' },
          { kind: 'block', type: 'beginner_forever' },
        ],
      },
      buildBeginnerLedCategory(),
      buildBeginnerSwitchCategory(),
      buildBeginnerSoundCategory(boardId),
      buildBeginnerDisplayCategory(),
      buildBeginnerSerialMonitorCategory(),
      {
        kind: 'category',
        name: 'Time',
        colour: String(BEGINNER_TIME_COLOR),
        cssconfig: categoryCssConfig('time'),
        contents: [
          { kind: 'block', type: 'beginner_wait' },
          { kind: 'block', type: 'beginner_wait_until' },
          { kind: 'block', type: 'beginner_current_time' },
          { kind: 'block', type: 'beginner_reset_timer' },
          { kind: 'block', type: 'beginner_timer_value' },
          { kind: 'block', type: 'beginner_has_time_passed' },
          { kind: 'block', type: 'beginner_every' },
        ],
      },
      {
        kind: 'category',
        name: 'Logic',
        colour: String(LOGIC_COLOR),
        cssconfig: categoryCssConfig('logic'),
        contents: [
          { kind: 'block', type: 'controls_if' },
          { kind: 'block', type: 'logic_compare' },
          { kind: 'block', type: 'logic_operation' },
          { kind: 'block', type: 'logic_negate' },
          { kind: 'block', type: 'logic_boolean' },
        ],
      },
      {
        kind: 'category',
        name: 'Loops',
        colour: String(LOOPS_COLOR),
        cssconfig: categoryCssConfig('loop'),
        contents: [
          { kind: 'block', type: 'controls_repeat_ext' },
          { kind: 'block', type: 'beginner_forever' },
          { kind: 'block', type: 'beginner_repeat_until' },
          { kind: 'block', type: 'beginner_break_loop' },
        ],
      },
      {
        kind: 'category',
        name: 'Variables',
        colour: String(VAR_COLOR),
        cssconfig: categoryCssConfig('variables'),
        custom: 'SKYROVER_VARIABLES',
      },
      {
        kind: 'category',
        name: 'Math',
        colour: String(MATH_COLOR),
        cssconfig: categoryCssConfig('math'),
        contents: [
          {
            kind: 'block',
            type: 'math_number',
            fields: { NUM: 0 },
          },
          { kind: 'block', type: 'math_arithmetic' },
          {
            kind: 'block',
            type: 'math_random_int',
            inputs: {
              FROM: { shadow: { type: 'math_number', fields: { NUM: 1 } } },
              TO: { shadow: { type: 'math_number', fields: { NUM: 100 } } },
            },
          },
          {
            kind: 'block',
            type: 'beginner_math_convert_range',
            inputs: {
              VALUE: { shadow: { type: 'math_number', fields: { NUM: 512 } } },
              FROM_LOW: { shadow: { type: 'math_number', fields: { NUM: 0 } } },
              FROM_HIGH: { shadow: { type: 'math_number', fields: { NUM: 1023 } } },
              TO_LOW: { shadow: { type: 'math_number', fields: { NUM: 0 } } },
              TO_HIGH: { shadow: { type: 'math_number', fields: { NUM: 255 } } },
            },
          },
          {
            kind: 'block',
            type: 'beginner_math_limit_value',
            inputs: {
              VALUE: { shadow: { type: 'math_number', fields: { NUM: 50 } } },
              LOW: { shadow: { type: 'math_number', fields: { NUM: 0 } } },
              HIGH: { shadow: { type: 'math_number', fields: { NUM: 100 } } },
            },
          },
        ],
      },
    ],
  };
}
